import React, { useState, useEffect } from "react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import { Grid } from "fluentui-react-grid";
import {
  Customer,
  CustomerDbConnectivity,
  CustomerModel,
  CustomerType,
  addCustomer,
  getCarrierCustomerConnectivity,
  getShipperCustomerConnectivity,
  updateCustomer,
} from "@/api/customers";
import TextInput from "@/ui-controls/TextInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Dropdown,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Dialog,
  DialogType,
  DialogFooter,
} from "@fluentui/react";

interface Props {
  onSave?: (newCustomer: Customer) => any;
  onUpdate?: () => any;
  onClose?: () => any;
  customer?: Customer;
}

const options: IDropdownOption[] = [
  {
    key: 1,
    text: "Carrier",
    selected: true,
  },
  {
    key: 2,
    text: "Shipper",
  },
];

const NewCustomerForm = (props: Props) => {
  const [isEditing] = useState<boolean>(props.customer !== undefined);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<number>(
    props.customer?.CustomerTypeId ?? 1
  ); // Default to "Carrier"
  const [connectivityMessageBarType, setConnectivityMessageBarType] = useState<
    MessageBarType | undefined
  >(undefined);
  const [customerDbConnectivity, setCustomerDbConnectivity] = useState<
    CustomerDbConnectivity | undefined
  >(undefined);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(
    null
  );

  useEffect(() => {
    return () => {};
  }, []);

  const saveCustomer = (customer: CustomerModel) => {
    setIsSaving(true);

    if (isEditing && props.customer) {
      updateCustomer(props.customer?.Id, customer).then(
        (result) => {
          setIsSaving(false);
          if (props.onUpdate) props.onUpdate();
        },
        (error) => {
          setIsSaving(false);
          handleError(error);
        }
      );
      return;
    }

    addCustomer(customer).then(
      (result) => {
        let customer = {
          ...result,
          UsersCount: 0,
        };
        setIsSaving(false);
        if (props.onSave) props.onSave(customer);
      },
      (error) => {
        setIsSaving(false);
        handleError(error);
      }
    );
  };

  const formik = useFormik({
    initialValues: {
      name: props.customer?.Name || "",
      description: props.customer?.Description || "",
      customerNumber: props.customer?.CustomerNumber || null,
      databaseIdentifier: props.customer?.DatabaseIdentifier || "",
      customerTypeId: props.customer?.CustomerTypeId || 1, // Default to Carrier
      environment: props.customer?.Environment || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required").max(250),
      description: Yup.string().max(250),
      customerNumber: Yup.mixed().when("customerTypeId", {
        is: (customerTypeId: number) => customerTypeId === CustomerType.Carrier,
        then: Yup.number()
          .transform((value, originalValue) =>
            String(originalValue).trim() === "" ? undefined : value
          )
          .typeError("Customer Number must be a number")
          .required("Customer Number is required for Carrier"),
        otherwise: Yup.mixed().notRequired(),
      }),
      databaseIdentifier: Yup.string()
        .max(100)
        .required("Database Identifier is required"),
      customerTypeId: Yup.number().required(),
      environment: Yup.string()
        .max(500)
        .when("customerTypeId", {
          is: (customerTypeId: number) =>
            customerTypeId === CustomerType.Shipper,
          then: Yup.string().required("Environment is required for Shipper"),
          otherwise: Yup.string().notRequired(),
        }),
    }),
    onSubmit: (values) => {
      const customerType = values.customerTypeId;
      let customerNumber = values.customerNumber
        ? Number(values.customerNumber)
        : undefined;
      let databaseIdentifier = values.databaseIdentifier.trim();
      let environment = values.environment.trim();

      if (customerType === CustomerType.Carrier) {
        environment = "";
      } else if (customerType === CustomerType.Shipper) {
        customerNumber = undefined;
      }

      const customer = {
        Name: values.name,
        Description: values.description,
        CustomerNumber: customerNumber,
        DatabaseIdentifier: databaseIdentifier,
        CustomerTypeId: customerType,
        Environment: environment,
      } as CustomerModel;
      saveCustomer(customer);
    },
  });

  const onCancelForm = () => {
    if (props.onClose) props.onClose();
  };

  const handleError = (error: any) => {
    const message =
      error?.response?.data ??
      error?.message ??
      "An error occurred while saving the customer.";
    setErrorDialogMessage(message);
    setIsErrorDialogOpen(true);
  };

  const getCustomerConnectivity = () => {
    setCustomerDbConnectivity(undefined);
    if (selectedType === 1) {
      // Carrier
      if (!formik.values.customerNumber || !formik.values.databaseIdentifier) {
        return;
      }

      setIsLoading(true);
      getCarrierCustomerConnectivity(
        formik.values.customerNumber,
        formik.values.databaseIdentifier
      )
        .then((result) => {
          setCustomerDbConnectivity(result);
          if (result.IsHealthy) {
            setConnectivityMessageBarType(MessageBarType.success);
          } else {
            setConnectivityMessageBarType(MessageBarType.error);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          handleError(error);
          setIsLoading(false);
        });
    } else {
      // Shipper
      if (!formik.values.databaseIdentifier || !formik.values.environment) {
        return;
      }

      setIsLoading(true);
      getShipperCustomerConnectivity(
        formik.values.databaseIdentifier,
        formik.values.environment
      )
        .then((result) => {
          setCustomerDbConnectivity(result);
          if (result.IsHealthy) {
            setConnectivityMessageBarType(MessageBarType.success);
          } else {
            setConnectivityMessageBarType(MessageBarType.error);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          handleError(error);
          setIsLoading(false);
        });
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit} style={{ paddingTop: "25px" }}>
        <Grid dir="ltr">
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <Dropdown
                options={options}
                selectedKey={selectedType}
                onChange={(_, option) => {
                  if (option) {
                    setSelectedType(option.key as number);
                    formik.setFieldValue("customerTypeId", Number(option.key)); // Sync with formik
                  }
                }}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Name"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("name"),
                }}
                hasError={formik.errors.name && formik.touched.name}
                errorMessage={formik.errors.name}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Description"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("description"),
                }}
                hasError={
                  formik.errors.description && formik.touched.description
                }
                errorMessage={formik.errors.description}
              />
            </Grid.Col>
          </Grid.Row>
          {selectedType === 1 && (
            <>
              <Grid.Row>
                <Grid.Col sizeSm={12}>
                  <TextInput
                    label="Customer Number"
                    baseTypeProps={{
                      disabled: isSaving,
                      ...formik.getFieldProps("customerNumber"),
                    }}
                    hasError={
                      formik.errors.customerNumber &&
                      formik.touched.customerNumber
                    }
                    errorMessage={formik.errors.customerNumber}
                  />
                </Grid.Col>
              </Grid.Row>
            </>
          )}

          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="Database Identifier"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("databaseIdentifier"),
                }}
                hasError={
                  formik.errors.databaseIdentifier &&
                  formik.touched.databaseIdentifier
                }
                errorMessage={formik.errors.databaseIdentifier}
              />
            </Grid.Col>
          </Grid.Row>

          {selectedType === 2 && (
            <Grid.Row>
              <Grid.Col sizeSm={12}>
                <TextInput
                  label="Environment"
                  baseTypeProps={{
                    disabled: isSaving,
                    ...formik.getFieldProps("environment"),
                  }}
                  hasError={
                    formik.errors.environment && formik.touched.environment
                  }
                  errorMessage={formik.errors.environment}
                />
              </Grid.Col>
            </Grid.Row>
          )}

          <Grid.Row className="panel-buttons">
            <Grid.Col>
              <PrimaryButton
                text="Test Connectivity"
                disabled={isSaving}
                onClick={getCustomerConnectivity}
              />
            </Grid.Col>
            <Grid.Col>
              <SaveButton
                text="Save"
                baseTypeProps={{ type: "submit", disabled: isSaving }}
              />
            </Grid.Col>
            <Grid.Col>
              <CancelButton
                text="Cancel"
                onClick={onCancelForm}
                baseTypeProps={{ disabled: isSaving }}
              />
            </Grid.Col>
          </Grid.Row>
          {customerDbConnectivity && !isLoading && (
            <Grid.Row>
              <Grid.Col sizeSm={12}>
                <MessageBar messageBarType={connectivityMessageBarType}>
                  {customerDbConnectivity.Message}
                  <p>{customerDbConnectivity.MaskedConnectionString}</p>
                </MessageBar>
              </Grid.Col>
            </Grid.Row>
          )}
          {isLoading && (
            <Grid.Row>
              <Grid.Col sizeSm={12}>
                <MessageBar messageBarType={MessageBarType.info}>
                  Testing connectivity, please wait...
                </MessageBar>
              </Grid.Col>
            </Grid.Row>
          )}
        </Grid>
      </form>
      <Dialog
        hidden={!isErrorDialogOpen}
        onDismiss={() => setIsErrorDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Unable to save customer",
          subText: errorDialogMessage ?? undefined,
        }}
        modalProps={{
          isBlocking: false,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={() => setIsErrorDialogOpen(false)}
            text="OK"
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default NewCustomerForm;
